import { useSyncExternalStore, useMemo } from 'react';
import * as Y from 'yjs';
import { getGraphName, getMetaMap } from '@glueapp/graph-collab';

/**
 * Subscribe to the graph name stored in the Y.Doc's `meta` map.
 *
 * The name is seeded server-side on document load and edited via `setGraphName`,
 * so it converges across all connected clients like any other doc state.
 * Returns `undefined` until the doc has synced and the name has been seeded.
 */
export function useYDocGraphName(yDoc: Y.Doc | null): string | undefined {
    const { subscribe, getSnapshot } = useMemo(() => {
        if (!yDoc) {
            return { subscribe: () => () => undefined, getSnapshot: (): string | undefined => undefined };
        }
        const meta = getMetaMap(yDoc);
        return {
            subscribe: (notify: () => void) => {
                meta.observe(notify);
                return () => meta.unobserve(notify);
            },
            getSnapshot: (): string | undefined => getGraphName(yDoc),
        };
    }, [yDoc]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
